import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { IActionSelectedUsers } from '../classes/models/primaryUsersModels'

import { actionApi } from './api/actionApi'
import { actionTransitionApi } from './api/actionTransitionApi'
import { contactApi } from './api/contactApi'
import { processesProgressApi } from './api/processesProgressApi'
import { progressApi } from './api/progressApi'
import { requestApi } from './api/requestApi'
import { templateApi } from './api/templateApi'
import { getInitialOnboardingState } from './config'
import './listeners'
import {
  getActionsFulfilled,
  getActionsPending,
  getActionsRejected,
  getParentActionsFulfilled,
  getParentActionsPending,
  getParentActionsRejected,
} from './reducers/actionsDataExtraReducer'
import {
  getChildrenRequestsFulfilled,
  getChildrenRequestsPending,
  getChildrenRequestsRejected,
} from './reducers/childrenRequestsDataExtraReducer'
import {
  getContactsWithMultiplePartnersFulfilled,
  getContactsWithMultiplePartnersPending,
  getContactsWithMultiplePartnersRejected,
} from './reducers/commonDataExtraReducer'
import {
  getExecutableTransitionFulfilled,
  getExecutableTransitionPending,
  getExecutableTransitionRejected,
} from './reducers/executableTransitionDataExtraReducer'
import {
  getParentRequestFulfilled,
  getParentRequestPending,
  getParentRequestRejected,
} from './reducers/parentRequestDataExtraReducer'
import {
  getProcessesProgressFulfilled,
  getProcessesProgressPending,
  getProcessesProgressRejected,
} from './reducers/processesProgressDataExtraReducer'
import {
  getContractSignatureProgressFulfilled,
  getContractSignatureProgressPending,
  getContractSignatureProgressRejected,
  getLRProgressFulfilled,
  getLRProgressPending,
  getLRProgressRejected,
  getProfilingProgressFulfilled,
  getProfilingProgressPending,
  getProfilingProgressRejected,
} from './reducers/progressDataExtraReducer'
import {
  getActionTransitionsFulfilled,
  getActionTransitionsPending,
  getActionTransitionsRejected,
} from './reducers/requestActionTransitionsDataExtraReducer'
import {
  getRequestFulfilled,
  getRequestPending,
  getRequestRejected,
} from './reducers/requestDataExtraReducer'
import {
  getRequestTemplateByRequestIdFulfilled,
  getRequestTemplateFulfilled,
  getRequestTemplatePending,
  getRequestTemplateRejected,
} from './reducers/requestTemplateDataExtraReducer'

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState: getInitialOnboardingState(),
  reducers: {
    resetOnboarding: () => getInitialOnboardingState(),
    setIsDataUploadedForTransitionsMapping: (state, action: PayloadAction<boolean>) => {
      const commonData = state.commonData
      commonData.isDataUploadedForTransitionsMapping = action.payload
    },
    changeActionSelectedUsers: (state, action: PayloadAction<IActionSelectedUsers>) => {
      const actions = state.actionsData.data
      const index = actions.findIndex((item) => item.EntryId === action.payload.entryId)
      if (index >= 0) {
        actions[index].SelectedBackupUser = action.payload.selectedBackupUser || ''
        actions[index].SelectedPrimaryUser = action.payload.selectedPrimaryUser || ''
      }
    },
    refreshContactData(state) {
      state.commonData.refreshContactDataTrigger = !state.commonData.refreshContactDataTrigger
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actionApi.getActions.pending, getActionsPending)
      .addCase(actionApi.getActions.rejected, getActionsRejected)
      .addCase(actionApi.getActions.fulfilled, getActionsFulfilled)

      .addCase(actionApi.getExecutableTransitions.pending, getExecutableTransitionPending)
      .addCase(actionApi.getExecutableTransitions.rejected, getExecutableTransitionRejected)
      .addCase(actionApi.getExecutableTransitions.fulfilled, getExecutableTransitionFulfilled)

      .addCase(actionApi.getParentActions.pending, getParentActionsPending)
      .addCase(actionApi.getParentActions.rejected, getParentActionsRejected)
      .addCase(actionApi.getParentActions.fulfilled, getParentActionsFulfilled)

      .addCase(
        contactApi.getContactsWithMultiplePartners.pending,
        getContactsWithMultiplePartnersPending
      )
      .addCase(
        contactApi.getContactsWithMultiplePartners.rejected,
        getContactsWithMultiplePartnersRejected
      )
      .addCase(
        contactApi.getContactsWithMultiplePartners.fulfilled,
        getContactsWithMultiplePartnersFulfilled
      )

      .addCase(processesProgressApi.getProcessesProgress.pending, getProcessesProgressPending)
      .addCase(processesProgressApi.getProcessesProgress.rejected, getProcessesProgressRejected)
      .addCase(processesProgressApi.getProcessesProgress.fulfilled, getProcessesProgressFulfilled)

      .addCase(requestApi.getRequestData.pending, getRequestPending)
      .addCase(requestApi.getRequestData.rejected, getRequestRejected)
      .addCase(requestApi.getRequestData.fulfilled, getRequestFulfilled)

      .addCase(requestApi.getChildrenRequests.pending, getChildrenRequestsPending)
      .addCase(requestApi.getChildrenRequests.rejected, getChildrenRequestsRejected)
      .addCase(requestApi.getChildrenRequests.fulfilled, getChildrenRequestsFulfilled)

      .addCase(requestApi.getParentRequest.pending, getParentRequestPending)
      .addCase(requestApi.getParentRequest.rejected, getParentRequestRejected)
      .addCase(requestApi.getParentRequest.fulfilled, getParentRequestFulfilled)

      .addCase(actionTransitionApi.getActionTransitions.pending, getActionTransitionsPending)
      .addCase(actionTransitionApi.getActionTransitions.rejected, getActionTransitionsRejected)
      .addCase(actionTransitionApi.getActionTransitions.fulfilled, getActionTransitionsFulfilled)

      .addCase(templateApi.getRequestTemplate.pending, getRequestTemplatePending)
      .addCase(templateApi.getRequestTemplate.rejected, getRequestTemplateRejected)
      .addCase(templateApi.getRequestTemplate.fulfilled, getRequestTemplateFulfilled)

      .addCase(templateApi.getRequestTemplateByRequestId.pending, getRequestTemplatePending)
      .addCase(templateApi.getRequestTemplateByRequestId.rejected, getRequestTemplateRejected)
      .addCase(
        templateApi.getRequestTemplateByRequestId.fulfilled,
        getRequestTemplateByRequestIdFulfilled
      )

      .addCase(progressApi.getProfilingProgress.pending, getProfilingProgressPending)
      .addCase(progressApi.getProfilingProgress.rejected, getProfilingProgressRejected)
      .addCase(progressApi.getProfilingProgress.fulfilled, getProfilingProgressFulfilled)

      .addCase(progressApi.getLRProgress.pending, getLRProgressPending)
      .addCase(progressApi.getLRProgress.rejected, getLRProgressRejected)
      .addCase(progressApi.getLRProgress.fulfilled, getLRProgressFulfilled)

      .addCase(
        progressApi.getContractSignatureProgress.pending,
        getContractSignatureProgressPending
      )
      .addCase(
        progressApi.getContractSignatureProgress.rejected,
        getContractSignatureProgressRejected
      )
      .addCase(
        progressApi.getContractSignatureProgress.fulfilled,
        getContractSignatureProgressFulfilled
      )
  },
})

export const {
  resetOnboarding,
  changeActionSelectedUsers,
  setIsDataUploadedForTransitionsMapping,
  refreshContactData,
} = onboardingSlice.actions
export const onboardingReducer = onboardingSlice.reducer
